// contactTableVarianten.ts — was „Kontakte" und „CO intern" unterscheidet.
//
// Beide Menüpunkte zeigen DIESELBE Tabelle (`ContactTableRedesign`). Die
// Unterschiede stehen gebündelt hier und nicht als Verzweigungen quer durch
// eine 1200-Zeilen-Komponente: Beim Klonen der Komponente wäre jede spätere
// Änderung an der Kontakttabelle zweimal zu machen — und beim zweiten Mal
// vergessen worden.
//
// Bewusst ohne React/DOM, damit die Konfiguration per Vitest prüfbar ist — wie
// `sonstigeFilterModel.ts` und `verantwortlichkeit/rollenEinordnung.ts`.

import {
  COINTERN_TABLE_COLUMN_ORDER_SCOPE,
  COINTERN_TABLE_COLUMN_VISIBILITY_SCOPE,
  COINTERN_TABLE_COLUMN_WIDTHS_SCOPE,
  CONTACT_TABLE_COLUMN_ORDER_SCOPE,
  CONTACT_TABLE_COLUMN_VISIBILITY_SCOPE,
  CONTACT_TABLE_COLUMN_WIDTHS_SCOPE,
} from "../../../utils/userPreferences";
import { CO_HALBJAHRE } from "../../../data/coInternKatalog";

export type TabellenVariante = "kontakte" | "cointern";

/**
 * Spalten-Id der Typ-Spalte in „CO intern".
 *
 * Eine EIGENE Id und nicht „Typ": An der Id hängt der Filterschlüssel, der ans
 * Backend geht. „Typ" filtert dort nach Firma/Person, `CoArt` nach der
 * Geschäftsbeziehung — beides sind Spaltenfilter derselben Stelle, aber nicht
 * dieselbe Frage.
 */
export const CO_ART_SPALTE = "CoArt";

/**
 * Die Spalten, die aus der „Lenkungsliste Vertriebs- und Produktpartner sowie
 * Lieferanten" stammen — in der Reihenfolge, in der sie dort stehen.
 *
 * Die Reihenfolge ist nicht Geschmack: Wer die Mappe kennt, sucht die Felder
 * an der gewohnten Stelle. Gelesen wird von links nach rechts „was ist das —
 * was liefern sie — wo steht es — was kostet es — wie lange — wie gut".
 *
 * Das LOGO steht bewusst nicht dabei: Es sitzt vorn in der Namensspalte, weil
 * es die Marke derselben Organisation ist, deren Name daneben steht — als
 * eigene Spalte stünde dieselbe Auskunft zweimal nebeneinander und kostete
 * 90 Pixel in einer Liste, die ohnehin zwanzig Spalten trägt.
 */
export const CO_SPALTEN: string[] = [
  "CoKategorie",
  "CoLeistung",
  "CoSharePoint",
  "CoWebseite",
  "CoPortal",
  "CoVertragsnummer",
  "CoQmRelevant",
  "CoVertragsart",
  "CoKosten",
  "CoBeginn",
  "CoAblauf",
  "CoTurnus",
  "CoAutoVerlaengerung",
  "CoKuendigungsfrist",
  "CoErstbewertung",
  "CoBewertung",
  // Eine Spalte je Halbjahr, wie in der Mappe (Spalte T bis AF).
  ...CO_HALBJAHRE.map((h) => h.spalte),
  "CoAktiv",
];

/** Reihenfolge-fähige (nicht-pinned) Spalten der gewöhnlichen Kontaktliste. */
export const SPALTEN_KONTAKTE: string[] = [
  "Ort",
  "kmoeglichkeit",
  "Kontaktstatus",
  "verantwortlich",
  "Vorgange",
  "Kundenverbindungen",
  "Relevanz",
  "Wert",
];

/**
 * Dieselben Spalten für „CO intern", davor die der Lenkungsliste.
 *
 * Eine Spalte „CO-Art" (Lieferant / Mitgliedschaft / Sponsoring /
 * Direktionspartner) gibt es hier NICHT: Dieselbe Auskunft trägt schon die
 * Kategorie der Mappe — ein Verein steht dort unter „Verband/Verein", ein
 * Lieferant unter seiner Warengruppe. Das Feld `coArten` am Kontakt bleibt
 * davon unberührt; es steuert weiterhin, wer die Zeile überhaupt sieht.
 *
 * „Kundenverbindungen" bleibt drin: Ein Lieferant kann sehr wohl in einem
 * Kundenverbund stehen.
 */
export const SPALTEN_COINTERN: string[] = [
  ...CO_SPALTEN,
  "Ort",
  "kmoeglichkeit",
  "Kontaktstatus",
  "verantwortlich",
  "Vorgange",
  "Kundenverbindungen",
  "Relevanz",
  "Wert",
];

/**
 * Was beim ersten Öffnen von „CO intern" ausgeblendet ist.
 *
 * Die Felder der Lenkungsliste bleiben ALLE sichtbar — sie sind der Grund, aus
 * dem jemand diese Liste öffnet. Ausgeblendet sind die Spalten der
 * Kundenliste, die hier fachlich ins Leere laufen: Ein Lieferant hat keine
 * K-Relevanz, keinen K-Wert und keinen Betreuer im Sinne des Kundenbestands.
 * Sie bleiben zuschaltbar — ein Direktionspartner KANN ein Kundenverbund sein.
 *
 * Ohne diese Vorauswahl stünden 28 Spalten nebeneinander, und die Liste wäre
 * beim Öffnen unbrauchbar.
 */
export const COINTERN_STANDARD_AUSGEBLENDET: string[] = [
  "Ort",
  "kmoeglichkeit",
  "Kontaktstatus",
  "verantwortlich",
  "Vorgange",
  "Kundenverbindungen",
  "Relevanz",
  "Wert",
];

export type VariantenKonfiguration = {
  /** Text im Suchfeld der Kopfleiste. */
  suchePlatzhalter: string;
  /**
   * Filter, der IMMER mitgeschickt wird.
   *
   * Er wird als LETZTES in das Filterobjekt gemischt, damit kein Spaltenfilter
   * ihn stumm überschreiben kann, und benutzt einen eigenen Backend-Schlüssel
   * statt `KontaktstatusIn` (den belegt der Vorfilter „Sonstige Kontakte"
   * schon).
   */
  festerFilter: Record<string, unknown>;
  /** Vorfilter „Sonstige Kontakte" in der Sub-Leiste. */
  zeigtSonstigeVorfilter: boolean;
  /** Kopfleisten-Knöpfe Datenbereinigung und Papierkorb. */
  zeigtKopfAktionen: boolean;
  /**
   * Umschalter „Kundenverbindungen anzeigen".
   *
   * Die Kundenverbindungen-Ansicht gruppiert den KUNDENbestand nach Verbünden.
   * Für Lieferanten und Vereine beantwortet sie keine Frage, die jemand in
   * dieser Liste stellt.
   */
  zeigtKundenverbindungen: boolean;
  /**
   * Die (eingefrorene) Typ-Spalte zeigt die CO-Art statt Firma/Person.
   *
   * In „CO intern" ist praktisch jede Zeile eine Firma — die Spalte stünde
   * 140-mal mit demselben Wort da. Die Frage an dieser Stelle lautet
   * „Lieferant oder Mitgliedschaft?", und genau die beantwortet sie hier.
   */
  typSpalteZeigtCoArt: boolean;
  /**
   * Zeigt die Spalten der Lenkungsliste (`CO_SPALTEN`).
   *
   * Hängt zusammen mit `includeTokens`: Ohne `cointern` im `?include=` liefert
   * der Server die Angaben gar nicht erst mit, und die Spalten blieben leer.
   */
  zeigtCoSpalten: boolean;
  /**
   * Was an `?include=` geht.
   *
   * Die gewöhnliche Kontaktliste fordert die Angaben der Lenkungsliste NICHT
   * an — ein Join und ein spürbar grösserer Rumpf je Zeile für Daten, die
   * dort niemand sieht.
   */
  includeTokens: string;
  /** Spalten, die beim ersten Öffnen ausgeblendet sind. */
  standardAusgeblendet: string[];
  /** Überschrift über den Einträgen des „Neu"-Menüs. */
  anlageTitel: string;
  /**
   * Schlüssel des Tabellenzustands im sessionStorage (Rücksprung aus der Akte).
   *
   * Eigener Schlüssel je Variante — sonst erbt die CO-Liste beim Rücksprung
   * Suche, Spaltenfilter, Vorfilter und Scroll-Position der Kontaktliste,
   * inklusive eines Vorfilters, den sie gar nicht anzeigt und den dann niemand
   * mehr abschalten kann.
   */
  navStateKey: string;
  /** Getrennte Preference-Scopes, siehe unten. */
  prefScopes: { order: string; visibility: string; widths: string };
  /** Reihenfolge-fähige Spalten dieser Variante. */
  spaltenReihenfolge: string[];
};

/**
 * Die beiden Varianten.
 *
 * Die getrennten Preference-Scopes sind nicht Kosmetik: `isValidOrder`
 * verwirft eine gespeicherte Spaltenreihenfolge, deren LÄNGE nicht passt. Mit
 * einem gemeinsamen Scope würde die 9er-Reihenfolge der CO-Liste die 8er der
 * Kontaktliste bei jedem Wechsel zurücksetzen — und Spaltenbreiten der einen
 * verschöben die andere.
 */
export const CONTACT_TABLE_VARIANTEN: Record<TabellenVariante, VariantenKonfiguration> = {
  kontakte: {
    suchePlatzhalter: "Suche in Kontakten …",
    // Reine CO-Kontakte raus, Doppelrollen (Kunde/Interessent/Altkunde)
    // bleiben. Das gilt AUCH für Berechtigte: Sonst sähe ausgerechnet die
    // Geschäftsführung die Lieferanten weiterhin zwischen den Kunden, und
    // genau das war die Beschwerde.
    festerFilter: { CoInternAusblenden: true },
    zeigtSonstigeVorfilter: true,
    zeigtKopfAktionen: true,
    zeigtKundenverbindungen: true,
    typSpalteZeigtCoArt: false,
    zeigtCoSpalten: false,
    includeTokens: "group",
    standardAusgeblendet: [],
    anlageTitel: "Kontakt anlegen",
    navStateKey: "contacts",
    prefScopes: {
      order: CONTACT_TABLE_COLUMN_ORDER_SCOPE,
      visibility: CONTACT_TABLE_COLUMN_VISIBILITY_SCOPE,
      widths: CONTACT_TABLE_COLUMN_WIDTHS_SCOPE,
    },
    spaltenReihenfolge: SPALTEN_KONTAKTE,
  },
  cointern: {
    suchePlatzhalter: "Suche in CO intern …",
    festerFilter: { CoIntern: true },
    // Der Vorfilter „Sonstige Kontakte" fällt weg: Er blendet Gruppen aus, die
    // es in dieser Liste ohnehin nicht gibt. Seine Aufgabe übernimmt hier der
    // Spaltenfilter „Kategorie".
    zeigtSonstigeVorfilter: false,
    // Datenbereinigung und Papierkorb gehören zur Pflege des Kundenbestands,
    // nicht zu den eigenen Geschäftsbeziehungen.
    zeigtKopfAktionen: false,
    zeigtKundenverbindungen: false,
    typSpalteZeigtCoArt: true,
    zeigtCoSpalten: true,
    includeTokens: "group,cointern",
    standardAusgeblendet: COINTERN_STANDARD_AUSGEBLENDET,
    anlageTitel: "CO-Kontakt anlegen",
    navStateKey: "cointern",
    prefScopes: {
      order: COINTERN_TABLE_COLUMN_ORDER_SCOPE,
      visibility: COINTERN_TABLE_COLUMN_VISIBILITY_SCOPE,
      widths: COINTERN_TABLE_COLUMN_WIDTHS_SCOPE,
    },
    spaltenReihenfolge: SPALTEN_COINTERN,
  },
};

/** Konfiguration zu einer Variante; unbekannte Werte fallen auf „kontakte". */
export const variantenKonfiguration = (
  variante: TabellenVariante | undefined,
): VariantenKonfiguration =>
  CONTACT_TABLE_VARIANTEN[variante ?? "kontakte"] ?? CONTACT_TABLE_VARIANTEN.kontakte;

/**
 * Anfangszustand der Spalten-Sichtbarkeit einer Variante.
 *
 * `{}` heisst „alles sichtbar"; nur ein ausdrückliches `false` blendet aus.
 * Gespeicherte Präferenzen des Nutzers überschreiben das Ergebnis später
 * vollständig — diese Vorauswahl gilt also nur, bis jemand selbst wählt.
 */
export const initialeSichtbarkeit = (
  konf: VariantenKonfiguration,
): Record<string, boolean> =>
  Object.fromEntries(konf.standardAusgeblendet.map((id) => [id, false]));
